import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Grow,
  Hidden,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  SvgIcon,
  TextField,
  Typography,
  useScrollTrigger,
} from "@material-ui/core";
import { Add as AddIcon, MusicOff as MusicOffIcon } from "@material-ui/icons";
import makeStyles from "@material-ui/core/styles/makeStyles";
import YouTube from "react-youtube";
import filters from "utils/search";
import SongMenuButton from "components/SongMenuButton";
import { UserContext } from "contexts/UserContext";
import BibleReferenceButton from "components/BibleReference/BibleReferenceButton";
import SongAvatar from "components/SongAvatar";
import NoLyricsWarning from "components/NoLyricsWarning";
import { hasLyrics } from "utils/lyrics";
import CatalogProgress from "components/CatalogProgress";
import LyricsThanks from "components/LyricsThanks";
import SongEditPage from "routes/SongDetail/SongEditPage";
import songsAPI from "restApi/songsApi";

const useStyles = makeStyles((theme) => ({
  active: { backgroundColor: theme.palette.secondary.main },
  scroll: { maxHeight: "100%", overflow: "auto" },
  // Mobile only (desktop's search field already stays put - it lives
  // outside the height-constrained, independently-scrolling song list
  // via listScrollWrapper/scroll below). On mobile the whole page
  // scrolls as one, so without this the search box scrolled away with
  // everything else instead of staying reachable at the top. top
  // starts at 56 (mobileTopBar's real height - see the matching
  // comment in Page.module.scss) to sit right below the shared
  // Navigationbar - but that bar hides itself on scroll down (see
  // Navigationbar's own HideOnScroll), so this needs to slide up to
  // fill the gap it leaves rather than sitting still below a bar
  // that's no longer there. Catalog computes the same
  // useScrollTrigger() Navigationbar uses and switches this to top: 0
  // in lockstep - the transition here is what makes that read as one
  // continuous header sliding up, rather than a jump once the bar's own
  // Slide animation finishes.
  stickySearch: {
    position: "sticky",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    transition: theme.transitions.create("top"),
  },
  searchField: {
    flexGrow: 1,
  },
  listScrollWrapper: {
    height: "calc(100vh - 132px)",
  },
  mediaScrollWrapper: {
    position: "relative",
    height: "calc(100vh - 482px)",
  },
  youtubeBox: {
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    paddingBottom: "20px",
  },
  youtube: { alignContent: "center", justify: "center", flex: 1 },
  lyrics: { whiteSpace: "pre-wrap", overflow: "auto", maxHeight: "100%" },
  spotifyButton: {
    transition: "0.2s ease-in-out",
    "&:hover": {
      color: "#1DB954",
    },
  },
  actionButtons: {
    position: "absolute",
    top: 0,
    right: 0,
  },
  emptyState: {
    textAlign: "center",
    paddingTop: theme.spacing(8),
    color: theme.palette.text.secondary,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
  },
}));

// The catalog's song list leaves out some fields the edit form needs (the
// YouTube link), so the form is given the full song, fetched when it opens -
// same as the edit page (SongEditContainer) does.
const EditPane = ({ song, onSaved, onCancel }) => {
  const [full, setFull] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    let current = true;
    setFull(null);
    setFailed(false);
    songsAPI.getSong(song.id).then(
      (res) => current && setFull(res.data),
      () => current && setFailed(true)
    );
    return () => {
      current = false;
    };
  }, [song.id]);
  if (failed) {
    return (
      <Box mt={4} textAlign="center">
        <Typography gutterBottom>Couldn't load this song to edit.</Typography>
        <Button onClick={onCancel}>Back</Button>
      </Box>
    );
  }
  if (!full) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }
  return <SongEditPage embedded song={full} onSaved={onSaved} onCancel={onCancel} />;
};

export default (props) => {
  const userContext = React.useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  // Same trigger (default threshold/hysteresis) Navigationbar's own
  // HideOnScroll uses, so this reads its hidden/shown state without
  // any actual coupling between the two components.
  const hideTopBar = useScrollTrigger();
  const [state, setState] = React.useState({
    selected: props.songs ? props.songs[0] : null,
    query: null,
    // Only the songs that still need lyrics (the shortcut in CatalogProgress).
    onlyMissing: false,
    // Desktop: the selected song is being edited in the detail pane.
    editing: false,
  });

  let songs = props.songs;
  const classes = useStyles();

  const searchField = (
    <TextField
      fullWidth
      className={classes.searchField}
      value={state.query || ""}
      label="Search"
      color="secondary"
      onChange={(e) => setState({ ...state, query: e.target.value })}
    />
  );
  const { menuActions, events, canAddDirectly } = props;
  // On desktop the list and the song sit side by side, so Edit opens the
  // form in the song's pane instead of going to a page of its own.
  const desktopMenuActions = {
    ...menuActions,
    edit: (song) => setState((prev) => ({ ...prev, selected: song, editing: true })),
  };
  const stopEditing = () => setState((prev) => ({ ...prev, editing: false }));
  const handleSaved = (saved, info) => {
    props.onSongUpdated(saved);
    setState((prev) => ({ ...prev, selected: { ...prev.selected, ...saved }, editing: false }));
    // LyricsThanks shows its thank-you off this navigation state.
    if (info && info.addedLyrics) history.replace({ ...location, state: { ...location.state, lyricsThanks: true } });
  };
  const addSongButton = (
    <IconButton
      title="Add song to catalog"
      onClick={() =>
        history.push("/songs/new", { from: location, directToCatalog: canAddDirectly })
      }
    >
      <AddIcon />
    </IconButton>
  );

  // The shortcut only means something while some song still needs lyrics.
  const showMissingOnly = state.onlyMissing && props.songs.some((song) => !hasLyrics(song));

  if (showMissingOnly || state.query) {
    let base = props.songs;
    if (showMissingOnly) base = base.filter((song) => !hasLyrics(song));
    if (state.query) {
      const queryFilter = filters.queryFilter(state.query, (song) =>
        [song.title, song.artist].join(" ")
      );
      base = queryFilter(base);
    }
    songs = base;
    if (songs.length > 0 && (!state.selected || songs.every((s) => s.id !== state.selected.id))) {
      state.selected = songs[0];
    }
  }

  const progressCard = (
    <CatalogProgress
      songs={props.songs}
      onlyMissing={showMissingOnly}
      onToggle={() => setState({ ...state, onlyMissing: !state.onlyMissing })}
    />
  );

  const emptyState = (
    <Box className={classes.emptyState}>
      <MusicOffIcon className={classes.emptyIcon} />
      <Typography variant="h6">
        {props.songs.length === 0
          ? "No songs in the catalog yet"
          : "No songs match your search"}
      </Typography>
      <Typography variant="body2">
        {props.songs.length === 0
          ? "Add a song to get started."
          : "Try a different search term."}
      </Typography>
    </Box>
  );

  return (
    <Grid container spacing={4}>
      <Hidden lgUp>
        <Grid
          item
          xs={12}
          className={classes.stickySearch}
          style={{ top: hideTopBar ? 0 : 56 }}
        >
          {searchField}
          {addSongButton}
        </Grid>
        <Grid item xs={12}>
          {progressCard}
          {songs.length === 0 ? (
            emptyState
          ) : (
            <Box className={classes.scroll}>
              <Grow
                in={true}
                style={{ transformOrigin: "50% 0 0" }}
                timeout={500}
              >
                <List>
                  {songs.map((song) => (
                    <ListItem
                      key={song.id}
                      button
                      onClick={() =>
                        history.push(`/songs/${song.id}`, { from: location })
                      }
                    >
                      <ListItemAvatar>
                        <SongAvatar song={song} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={song.title}
                        secondary={
                          <>
                            {song.artist}
                            {!hasLyrics(song) && <NoLyricsWarning />}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        {!!(
                          userContext.user.is_team_leader |
                          userContext.user.is_staff
                        ) && (
                          <SongMenuButton
                            song={song}
                            actions={menuActions}
                            events={events}
                          />
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Grow>
            </Box>
          )}
        </Grid>
      </Hidden>
      <Hidden mdDown>
        <Grid item xs={4} className={classes.listScrollWrapper}>
          {progressCard}
          <Box display="flex" alignItems="center">
            {searchField}
            {addSongButton}
          </Box>
          {songs.length === 0 ? (
            emptyState
          ) : (
            <Box className={classes.scroll} mt={2}>
              <List>
                {songs.map((song) => {
                  return (
                    <ListItem
                      className={
                        state.selected.id === song.id ? classes.active : ""
                      }
                      key={song.id}
                      onClick={() => setState({ selected: song })}
                      button
                    >
                      <ListItemAvatar>
                        <SongAvatar song={song} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={song.title}
                        secondary={
                          <>
                            {song.artist}
                            {!hasLyrics(song) && (
                              <NoLyricsWarning inheritColor={state.selected.id === song.id} />
                            )}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        {!!(
                          userContext.user.is_team_leader |
                          userContext.user.is_staff
                        ) && (
                          <SongMenuButton
                            song={{
                              ...song,
                              youtube_link: song.youtube_link,
                              lyrics: song.lyrics,
                            }}
                            actions={desktopMenuActions}
                            events={events}
                          />
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Grid>
        <Grid item xs={8}>
          <Container maxWidth="md">
            {state.selected && state.editing ? (
              <Box>
                <EditPane
                  key={state.selected.id}
                  song={state.selected}
                  onSaved={handleSaved}
                  onCancel={stopEditing}
                />
              </Box>
            ) : (
              state.selected && (
              <>
                {state.selected && (
                  <YouTube
                    className={classes.youtube}
                    videoId={state.selected.youtube_id}
                    containerClassName={classes.youtubeBox}
                    opts={{
                      width: "100%",
                      playerVars: { start: state.selected.youtube_start || 0 },
                    }}
                  />
                )}
                <Box className={classes.mediaScrollWrapper}>
                  <Box className={classes.actionButtons}>
                    <BibleReferenceButton song={state.selected} />
                    {state.selected.spotify_id && (
                      <IconButton
                        href={`spotify:track:${state.selected.spotify_id}`}
                        title="Open in Spotify"
                        className={classes.spotifyButton}
                      >
                        <SvgIcon>
                          <path d="M19.098 10.638c-3.868-2.297-10.248-2.508-13.941-1.387-.593.18-1.22-.155-1.399-.748-.18-.593.154-1.22.748-1.4 4.239-1.287 11.285-1.038 15.738 1.605.533.317.708 1.005.392 1.538-.316.533-1.005.709-1.538.392zm-.126 3.403c-.272.44-.847.578-1.287.308-3.225-1.982-8.142-2.557-11.958-1.399-.494.15-1.017-.129-1.167-.623-.149-.495.13-1.016.624-1.167 4.358-1.322 9.776-.682 13.48 1.595.44.27.578.847.308 1.286zm-1.469 3.267c-.215.354-.676.465-1.028.249-2.818-1.722-6.365-2.111-10.542-1.157-.402.092-.803-.16-.895-.562-.092-.403.159-.804.562-.896 4.571-1.045 8.492-.595 11.655 1.338.353.215.464.676.248 1.028zm-5.503-17.308c-6.627 0-12 5.373-12 12 0 6.628 5.373 12 12 12 6.628 0 12-5.372 12-12 0-6.627-5.372-12-12-12z" />
                        </SvgIcon>
                      </IconButton>
                    )}
                  </Box>
                  <Typography className={classes.lyrics}>
                    {state.selected.lyrics || ""}
                  </Typography>
                </Box>
              </>
              )
            )}
          </Container>
        </Grid>
      </Hidden>
      <LyricsThanks />
    </Grid>
  );
};
